import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Sondage } from '../models/sondage.model';
import { SondageService } from '../services/sondage.service';
import { Parametre } from '../models/parametre.model';
import { ParametreService } from '../services/parametre.service';
import { LangService, Langue } from '../services/lang.service';

interface PosteCout {
  icone: string;
  label: string;
  sousse: number;
  ville: number;
  retournee: boolean;
}

type IconeMeteo = 'soleil' | 'lune' | 'nuage' | 'pluie' | 'orage' | 'neige' | 'brouillard';

@Component({
  selector: 'app-decouvrir',
  templateUrl: './decouvrir.component.html',
  styleUrls: ['./decouvrir.component.css']
})
export class DecouvrirComponent implements OnInit {

  // ---------- Comparateur de cout de la vie ----------
  // NOTE : chiffres illustratifs, pas des donnees de prix en temps reel.
  villes: { [cle: string]: { label: string; items: PosteCout[] } } = {
    paris: {
      label: 'Paris',
      items: [
        { icone: '☕', label: 'dec.cout.cafe', sousse: 0.6, ville: 4.5, retournee: false },
        { icone: '🍽', label: 'dec.cout.repas', sousse: 6, ville: 20, retournee: false },
        { icone: '🏨', label: 'dec.cout.hotel', sousse: 45, ville: 180, retournee: false },
        { icone: '🚕', label: 'dec.cout.taxi', sousse: 2.4, ville: 22, retournee: false },
        { icone: '🏠', label: 'dec.cout.studio', sousse: 240, ville: 950, retournee: false }
      ]
    },
    rome: {
      label: 'Rome',
      items: [
        { icone: '☕', label: 'dec.cout.cafe', sousse: 0.6, ville: 1.5, retournee: false },
        { icone: '🍽', label: 'dec.cout.repas', sousse: 6, ville: 16, retournee: false },
        { icone: '🏨', label: 'dec.cout.hotel', sousse: 45, ville: 140, retournee: false },
        { icone: '🚕', label: 'dec.cout.taxi', sousse: 2.4, ville: 18, retournee: false },
        { icone: '🏠', label: 'dec.cout.studio', sousse: 240, ville: 750, retournee: false }
      ]
    },
    londres: {
      label: 'Londres',
      items: [
        { icone: '☕', label: 'dec.cout.cafe', sousse: 0.6, ville: 4.2, retournee: false },
        { icone: '🍽', label: 'dec.cout.repas', sousse: 6, ville: 21, retournee: false },
        { icone: '🏨', label: 'dec.cout.hotel', sousse: 45, ville: 165, retournee: false },
        { icone: '🚕', label: 'dec.cout.taxi', sousse: 2.4, ville: 26, retournee: false },
        { icone: '🏠', label: 'dec.cout.studio', sousse: 240, ville: 1150, retournee: false }
      ]
    },
    berlin: {
      label: 'Berlin',
      items: [
        { icone: '☕', label: 'dec.cout.cafe', sousse: 0.6, ville: 3.6, retournee: false },
        { icone: '🍽', label: 'dec.cout.repas', sousse: 6, ville: 15, retournee: false },
        { icone: '🏨', label: 'dec.cout.hotel', sousse: 45, ville: 125, retournee: false },
        { icone: '🚕', label: 'dec.cout.taxi', sousse: 2.4, ville: 20, retournee: false },
        { icone: '🏠', label: 'dec.cout.studio', sousse: 240, ville: 780, retournee: false }
      ]
    }
  };

  villeChoisie = 'paris';

  get postesActuels(): PosteCout[] {
    return this.villes[this.villeChoisie].items;
  }

  get labelVille(): string {
    return this.villes[this.villeChoisie].label;
  }

  choisirVille(cle: string): void {
    this.villeChoisie = cle;
  }

  retournerPiece(poste: PosteCout): void {
    poste.retournee = !poste.retournee;
  }

  pourcentageEconomie(poste: PosteCout): number {
    return Math.round((1 - poste.sousse / poste.ville) * 100);
  }

  // ---------- Sondage : avis du public pour la prochaine edition ----------
  sondage: Sondage | null = null;
  aVote = false;
  sondageChargement = true;
  sondageErreur = false;
  sondageVoteEnCours = false;
  parametres: Parametre | null = null;

  // ---------- Meteo en direct (Open-Meteo — gratuit, sans cle API) ----------
  meteoChargement = true;
  meteoErreur = false;
  meteoTemperature: number | null = null;
  meteoDescription = '';
  meteoIcone: IconeMeteo = 'soleil';
  meteoHeureMaj = '';
  previsionSemaine: { jour: string; icone: IconeMeteo; max: number; min: number }[] = [];
  langueActuelle: Langue = 'fr';
  private donneesMeteoBrutes: any = null;

  constructor(
    private sondageService: SondageService,
    private parametreService: ParametreService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private langService: LangService
  ) { }

  ngOnInit(): void {
    this.chargerSondage();
    this.chargerMeteo();

    this.langService.langue$.subscribe(l => {
      this.langueActuelle = l;
      // Si la meteo est deja chargee, on ne refait pas d'appel API : on ne fait
      // que reformuler l'affichage (descriptions, jours) dans la nouvelle langue.
      if (this.donneesMeteoBrutes) this.appliquerDonneesMeteo(this.donneesMeteoBrutes);
    });

    this.parametreService.getParametres().subscribe(data => {
      this.parametres = data;
    });
  }

  // Coordonnees de Sousse, Tunisie — meteo actuelle + prevision sur 7 jours
  chargerMeteo(): void {
    this.meteoChargement = true;
    this.meteoErreur = false;

    const url = 'https://api.open-meteo.com/v1/forecast'
      + '?latitude=35.8256&longitude=10.6084'
      + '&current_weather=true'
      + '&daily=weathercode,temperature_2m_max,temperature_2m_min'
      + '&timezone=auto';

    this.http.get<any>(url).subscribe({
      next: (data) => {
        if (!data?.current_weather) { this.meteoErreur = true; this.meteoChargement = false; return; }
        this.donneesMeteoBrutes = data;
        this.appliquerDonneesMeteo(data);
        this.meteoChargement = false;
      },
      error: () => {
        this.meteoErreur = true;
        this.meteoChargement = false;
      }
    });
  }

  // Applique les donnees brutes de l'API dans la langue actuellement selectionnee
  private appliquerDonneesMeteo(data: any): void {
    const actuel = data.current_weather;

    this.meteoTemperature = Math.round(actuel.temperature);
    const { icone, description } = this.interpreterCodeMeteo(actuel.weathercode, actuel.is_day === 1);
    this.meteoIcone = icone;
    this.meteoDescription = description;

    const localeParLangue: Record<Langue, string> = { fr: 'fr-FR', en: 'en-GB', ar: 'ar-TN' };
    this.meteoHeureMaj = new Date().toLocaleTimeString(localeParLangue[this.langueActuelle], { hour: '2-digit', minute: '2-digit' });

    const jours = data.daily;
    if (jours?.time) {
      const nomsJoursParLangue: Record<Langue, string[]> = {
        fr: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
        en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        ar: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
      };
      const nomsJours = nomsJoursParLangue[this.langueActuelle];
      const aujourdhui: Record<Langue, string> = { fr: "Aujourd'hui", en: 'Today', ar: 'اليوم' };

      this.previsionSemaine = jours.time.map((dateISO: string, i: number) => {
        const date = new Date(dateISO + 'T12:00:00');
        return {
          jour: i === 0 ? aujourdhui[this.langueActuelle] : nomsJours[date.getDay()],
          icone: this.interpreterCodeMeteo(jours.weathercode[i], true).icone,
          max: Math.round(jours.temperature_2m_max[i]),
          min: Math.round(jours.temperature_2m_min[i])
        };
      });
    }
  }

  // Traduit les codes meteo standard (WMO) d'Open-Meteo en icone + phrase lisible, dans la langue active
  private interpreterCodeMeteo(code: number, estLeJour: boolean): { icone: IconeMeteo; description: string } {
    const L = this.langueActuelle;
    const t = (fr: string, en: string, ar: string) => (L === 'en' ? en : L === 'ar' ? ar : fr);

    if (code === 0) {
      return estLeJour
        ? { icone: 'soleil', description: t('Ciel dégagé — une veste légère suffit pour la soirée en plein air.', 'Clear skies — a light jacket is enough for the evening outdoors.', 'سماء صافية — سترة خفيفة تكفي لأمسية في الهواء الطلق.') }
        : { icone: 'lune', description: t('Nuit claire et étoilée — parfaite pour un spectacle en plein air.', 'Clear, starry night — perfect for an open-air show.', 'ليلة صافية ومرصعة بالنجوم — مثالية لعرض في الهواء الطلق.') };
    }
    if (code <= 2) {
      return estLeJour
        ? { icone: 'soleil', description: t('Grand ciel bleu avec quelques nuages — journée idéale à Sousse.', 'Mostly blue skies with a few clouds — an ideal day in Sousse.', 'سماء زرقاء صافية مع بعض الغيوم — يوم مثالي في سوسة.') }
        : { icone: 'lune', description: t('Ciel globalement dégagé ce soir, quelques passages nuageux.', 'Mostly clear tonight, with a few cloudy spells.', 'سماء صافية غالباً هذا المساء مع بعض الغيوم العابرة.') };
    }
    if (code === 3) return { icone: 'nuage', description: t('Ciel couvert, sans pluie prévue — la soirée reste agréable.', 'Overcast skies, no rain expected — still a pleasant evening.', 'سماء غائمة دون أمطار متوقعة — أمسية لطيفة رغم ذلك.') };
    if (code === 45 || code === 48) return { icone: 'brouillard', description: t('Brume légère en soirée — prévoyez une veste un peu plus chaude.', 'Light mist this evening — bring a slightly warmer jacket.', 'ضباب خفيف هذا المساء — يُنصح بسترة أكثر دفئاً.') };
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
      return { icone: 'pluie', description: t('Pluie possible — un imperméable léger est recommandé ce soir.', 'Rain possible — a light raincoat is recommended tonight.', 'احتمال هطول أمطار — يُنصح بمعطف واق خفيف هذا المساء.') };
    }
    if ((code >= 71 && code <= 77) || code === 85 || code === 86) {
      return { icone: 'neige', description: t('Précipitations inhabituelles pour la saison — pensez à vous couvrir.', 'Unusual precipitation for the season — dress warmly.', 'هطول غير معتاد لهذا الموسم — يُنصح بالتدفئة الجيدة.') };
    }
    if (code >= 95) return { icone: 'orage', description: t('Orage possible — les organisateurs suivent la météo de près.', 'Possible thunderstorm — organisers are monitoring conditions closely.', 'احتمال عاصفة رعدية — المنظمون يتابعون الطقس عن كثب.') };
    return { icone: 'nuage', description: t('Conditions changeantes ce soir à Sousse.', 'Changeable conditions in Sousse tonight.', 'أحوال جوية متغيرة هذا المساء في سوسة.') };
  }

  chargerSondage(): void {
    this.sondageChargement = true;
    this.sondageErreur = false;

    this.sondageService.getSondageActif().subscribe({
      next: (data) => {
        this.sondage = data;
        this.sondageChargement = false;
        // On verifie si l'utilisateur a deja vote sur CE sondage precis (stocke localement)
        const idsVotes = this.getIdsSondagesVotes();
        if (data && data.id && idsVotes.includes(data.id)) {
          this.aVote = true;
        }
      },
      error: () => {
        this.sondage = null;
        this.sondageChargement = false;
        this.sondageErreur = true;
      }
    });
  }

  totalVotes(): number {
    if (!this.sondage) return 0;
    return this.sondage.options.reduce((total, o) => total + o.nombreVotes, 0);
  }

  pourcentageOption(option: { nombreVotes: number }): number {
    const total = this.totalVotes();
    return total === 0 ? 0 : Math.round((option.nombreVotes / total) * 100);
  }

  voter(optionId: number | undefined): void {
    if (optionId === undefined || this.aVote || this.sondageVoteEnCours) return;

    this.sondageVoteEnCours = true;
    this.sondageService.voter(optionId).subscribe({
      next: () => {
        this.sondageVoteEnCours = false;
        this.aVote = true;
        this.ajouterIdSondageVote(this.sondage?.id);
        this.chargerSondage(); // recharge pour afficher les resultats a jour
      },
      error: () => {
        this.sondageVoteEnCours = false;
        alert("Votre vote n'a pas pu être enregistré. Vérifiez votre connexion et réessayez.");
      }
    });
  }

  private getIdsSondagesVotes(): number[] {
    const brut = localStorage.getItem('fis_sondages_votes');
    return brut ? JSON.parse(brut) : [];
  }

  private ajouterIdSondageVote(id: number | undefined): void {
    if (id === undefined) return;
    const ids = this.getIdsSondagesVotes();
    ids.push(id);
    localStorage.setItem('fis_sondages_votes', JSON.stringify(ids));
  }
}
